// components/PartnersSection.jsx
import React from 'react';

const PartnerLogo = ({ name, type, logoUrl }) => {
    // If logoUrl is provided, render image instead of custom elements
    if (logoUrl) {
        return (
            <div className="flex items-center justify-center md:w-52">
                <img
                    src={logoUrl}
                    alt={name}
                    className="object-contain invert-100 dark:invert-0 "
                />
            </div>
        );
    }

    const renderLogo = () => {
        switch (name) {
            case 'IONOS':
                return (
                    <div className="text-white text-2xl font-bold tracking-wider">
                        IONOS
                    </div>
                );

            case 'Trusted Shops Qualified Partner':
                return (
                    <div className="bg-white rounded-md px-4 py-3 flex flex-col items-center">
                        <div className="text-gray-800 text-lg font-semibold mb-1">
                            TRUSTED
                        </div>
                        <div className="text-gray-800 text-lg font-semibold mb-1">
                            SHOPS
                        </div>
                        <div className="text-gray-600 text-xs">
                            Qualified Partner
                        </div>
                    </div>
                );

            case 'Microsoft':
                return (
                    <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 grid grid-cols-2 gap-1">
                            <div className="bg-[#EE005B] w-2.5 h-2.5"></div>
                            <div className="bg-green-500 w-2.5 h-2.5"></div>
                            <div className="bg-blue-500 w-2.5 h-2.5"></div>
                            <div className="bg-yellow-500 w-2.5 h-2.5"></div>
                        </div>
                        <span className="text-white text-xl font-medium">Microsoft</span>
                    </div>
                );

            case 'Ranking Online':
                return (
                    <div className="flex items-center space-x-2">
                        <div className="bg-white p-2 rounded">
                            <div className="flex space-x-1">
                                <div className="w-2 h-6 bg-gray-800"></div>
                                <div className="w-2 h-4 bg-gray-800 mt-2"></div>
                                <div className="w-2 h-8 bg-gray-800 -mt-1"></div>
                            </div>
                        </div>
                        <div className="text-white">
                            <div className="text-lg font-semibold">Ranking</div>
                            <div className="text-lg font-semibold">Online</div>
                        </div>
                    </div>
                );

            case 'Market Valencia':
                return (
                    <div className="text-white text-xl font-bold">
                        <span className="bg-green-500 text-black px-2 py-1 rounded-sm mr-1">
                            MARKET
                        </span>
                        <div className="text-sm mt-1">VALENCIA</div>
                    </div>
                );

            case 'ceM - Código Ético de Marketing':
                return (
                    <div className="text-white">
                        <div className="text-3xl font-bold mb-1">ceM</div>
                        <div className="text-xs text-gray-300 leading-tight">
                            CÓDIGO ÉTICO<br />DE MARKETING
                        </div>
                    </div>
                );

            case 'Hostinger':
                return (
                    <div className="flex items-center text-white">
                        <div className="bg-purple-600 w-8 h-8 rounded mr-3 flex items-center justify-center">
                            <span className="text-white font-bold text-lg">H</span>
                        </div>
                        <span className="text-xl font-semibold">HOSTINGER</span>
                    </div>
                );

            case 'AMKT - Asociación de Marketing de España':
                return (
                    <div className="text-white text-center">
                        <div className="text-2xl font-bold mb-1">AMKT</div>
                        <div className="text-xs text-gray-300 leading-tight">
                            Asociación de Marketing<br />de España
                        </div>
                    </div>
                );

            case 'LinkedIn':
                return (
                    <div className="text-white text-xl font-semibold">
                        Linked<span className="bg-blue-600 text-white px-1 rounded">in</span>
                    </div>
                );

            case 'SEOnet':
                return (
                    <div className="text-white">
                        <span className="text-xl font-bold">SEO</span>
                        <span className="text-xl font-light">net</span>
                        <div className="w-8 h-1 bg-green-500 mt-1"></div>
                    </div>
                );

            default:
                return (
                    <div className="text-white text-lg font-medium">
                        {name}
                    </div>
                );
        }
    };

    return (
        <div className="flex items-center justify-center h-16 md:h-20">
            {renderLogo()}
        </div>
    );
};

const PartnerGrid = ({ partners }) => {
    return (
        <div className="space-y-12 w-full grid grid-cols-2 md:grid-cols-1 gap-6">
            {partners.map((row, rowIndex) => (
                <div
                    key={rowIndex}
                    className="md:flex w-full items-center justify-between grid grid-cols-1 gap-4 h-full md:h-fit"
                >
                    {row.map((partner, index) => (
                        <PartnerLogo
                            key={`${rowIndex}-${index}`}
                            name={partner.name}
                            type={partner.type}
                            logoUrl={partner.logoUrl}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

const PartnersSection = () => {
    // Logo URLs array - you can add your image URLs here
    const logoUrls = {
        'IONOS': '/new-final-data/home/partners/1.png',
        'Trusted Shops Qualified Partner': '/new-final-data/home/partners/2.png',
        'Microsoft': '/new-final-data/home/partners/3.png',
        'Ranking Online': '/new-final-data/home/partners/4.png',
        'Market Valencia': '/new-final-data/home/partners/5.png',
        'ceM - Código Ético de Marketing': '/new-final-data/home/partners/6.png',
        'Hostinger': '/new-final-data/home/partners/7.png',
        'AMKT - Asociación de Marketing de España': '/new-final-data/home/partners/8.png',
        'LinkedIn': '/new-final-data/home/partners/9.png',
        'SEOnet': '/new-final-data/home/partners/10.png'
    };

    const partners = [
        // Row 1
        [
            { name: 'IONOS', type: 'text', logoUrl: logoUrls['IONOS'] },
            { name: 'Trusted Shops Qualified Partner', type: 'badge', logoUrl: logoUrls['Trusted Shops Qualified Partner'] },
            { name: 'Microsoft', type: 'text', logoUrl: logoUrls['Microsoft'] },
            { name: 'Ranking Online', type: 'logo-text', logoUrl: logoUrls['Ranking Online'] },
            { name: 'Market Valencia', type: 'text', logoUrl: logoUrls['Market Valencia'] }
        ],
        // Row 2
        [
            { name: 'ceM - Código Ético de Marketing', type: 'logo-text', logoUrl: logoUrls['ceM - Código Ético de Marketing'] },
            { name: 'Hostinger', type: 'logo-text', logoUrl: logoUrls['Hostinger'] },
            { name: 'AMKT - Asociación de Marketing de España', type: 'logo-text', logoUrl: logoUrls['AMKT - Asociación de Marketing de España'] },
            { name: 'LinkedIn', type: 'text', logoUrl: logoUrls['LinkedIn'] },
            { name: 'SEOnet', type: 'logo-text', logoUrl: logoUrls['SEOnet'] }
        ]
    ];

    return (
        <section className="bg-white dark:bg-transparent py-20 md:px-35 px-10">
            <div className=" mx-auto">
                <h2 className="text-black dark:text-white text-4xl md:text-5xl font-bold text-center mb-16">
                    Certifications
                </h2>
                <PartnerGrid partners={partners} />
            </div>
        </section>
    );
};

export default PartnersSection;
